import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AdminPost, CreatePost, PostStatus, UpdatePost } from '@mys/contracts';
import { api } from './api.js';

/* ============================================================================
   The blog editor's queries.
   ----------------------------------------------------------------------------
   Everything here talks to `/api/admin/posts`, which is a different path from
   the public `/api/posts` the site reads. That separation is the whole
   protection against a draft reaching the public blog, so it is worth not
   quietly "tidying" the two into one client.
   ========================================================================== */

export const POST_STATUSES: PostStatus[] = ['draft', 'scheduled', 'published'];

export const POST_STATUS_LABELS: Record<PostStatus, string> = {
  draft: 'Draft',
  scheduled: 'Schedule kiya',
  published: 'Live',
};

/** Summary rows: the list endpoint leaves the body out. */
export type PostSummaryRow = Omit<AdminPost, 'bodyMarkdown'>;

export interface AdminPostListResponse {
  ok: true;
  items: PostSummaryRow[];
  total: number;
  page: number;
  limit: number;
  counts: Partial<Record<PostStatus, number>>;
}

export function usePosts(filters: { status?: PostStatus; q?: string }, limit = 100) {
  return useQuery({
    queryKey: ['admin-posts', filters, limit],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams();
      if (filters.status) params.set('status', filters.status);
      if (filters.q) params.set('q', filters.q);
      params.set('limit', String(limit));
      return api.get<AdminPostListResponse>(`/api/admin/posts?${params.toString()}`, signal);
    },
  });
}

export function usePost(id: string) {
  return useQuery({
    queryKey: ['admin-post', id],
    queryFn: ({ signal }) =>
      api.get<{ ok: true; post: AdminPost }>(`/api/admin/posts/${id}`, signal),
    enabled: id !== '',
  });
}

function invalidate(client: ReturnType<typeof useQueryClient>, id?: string) {
  void client.invalidateQueries({ queryKey: ['admin-posts'] });
  if (id) void client.invalidateQueries({ queryKey: ['admin-post', id] });
}

export function useCreatePost() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePost) =>
      api.post<{ ok: true; post: AdminPost }>('/api/admin/posts', input),
    onSuccess: () => invalidate(client),
  });
}

export function useUpdatePost(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (changes: UpdatePost) =>
      api.patch<{ ok: true; post: AdminPost }>(`/api/admin/posts/${id}`, changes),
    onSuccess: () => invalidate(client, id),
  });
}

export function useDeletePost(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.del<void>(`/api/admin/posts/${id}`),
    onSuccess: () => invalidate(client, id),
  });
}

/** Roughly how long a post takes to read. 200 words a minute, rounded up. */
export function readingMinutes(bodyMarkdown: string): number {
  const words = bodyMarkdown.trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.ceil(words / 200));
}
